import htmx from 'htmx.org'
import { WidgetInstance } from 'friendly-challenge'

// Campaign page: sticky donation block
htmx.onLoad(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      const stickyBlock = document.getElementById('donation-cta-fixed')

      if (stickyBlock) {
        stickyBlock.classList.toggle('hidden', entry.isIntersecting)
      }
    },
    { threshold: 0 }
  )

  const donationCtaRelative = document.getElementById('donation-cta-relative')

  if (donationCtaRelative) {
    observer.observe(donationCtaRelative)
  }

  // Campaign description show more/less toggle
  const descriptionToggle = document.querySelector('[data-description-toggle]')
  const descriptionContent = document.querySelector('.description-content')

  if (descriptionToggle && descriptionContent) {
    const showMoreText = descriptionToggle.getAttribute('data-show-more') || 'Show more'
    const showLessText = descriptionToggle.getAttribute('data-show-less') || 'Show less'

    descriptionToggle.addEventListener('click', () => {
      const isExpanded = descriptionContent.getAttribute('data-expanded') === 'true'

      if (isExpanded) {
        descriptionContent.classList.add('line-clamp-5')
        descriptionContent.setAttribute('data-expanded', 'false')
        descriptionToggle.textContent = showMoreText
      } else {
        descriptionContent.classList.remove('line-clamp-5')
        descriptionContent.setAttribute('data-expanded', 'true')
        descriptionToggle.textContent = showLessText
      }
    })
  }

  // Animate progress bars on load
  const progressBars = document.querySelectorAll('[data-progress]')
  progressBars.forEach((bar) => {
    const targetProgress = bar.getAttribute('data-progress')
    if (targetProgress) {
      // Small delay to ensure the transition happens
      setTimeout(() => {
        ;(bar as HTMLElement).style.width = `${targetProgress}%`
      }, 100)
    }
  })

  // Donation carousel auto-rotation
  const donationCarousels = document.querySelectorAll('[data-donation-carousel]')
  donationCarousels.forEach((carousel) => {
    const items = carousel.children
    const totalItems = items.length
    let currentIndex = 0

    if (totalItems > 1) {
      setInterval(() => {
        currentIndex = (currentIndex + 1) % totalItems
        const offset = currentIndex * -24 // -24px (h-6 = 1.5rem = 24px)
        ;(carousel as HTMLElement).style.transform = `translateY(${offset}px)`
      }, 5000)
    }
  })

  // Share link copy button
  const copyButton = document.querySelector('[data-copy-share-link]')
  if (copyButton) {
    copyButton.addEventListener('click', () => {
      const shareLink = document.getElementById('share-link') as HTMLInputElement
      const successMessage = document.getElementById('copy-success-message')

      if (shareLink) {
        navigator.clipboard.writeText(shareLink.value).then(() => {
          if (successMessage) {
            successMessage.classList.remove('hidden')
            setTimeout(() => {
              successMessage.classList.add('hidden')
            }, 3000)
          }
        })
      }
    })
  }

  // Facebook share button
  const facebookShareButton = document.querySelector('[data-facebook-share]')
  if (facebookShareButton) {
    facebookShareButton.addEventListener('click', () => {
      const shareUrl = facebookShareButton.getAttribute('data-facebook-share')
      if (shareUrl) {
        window.open(shareUrl, 'facebook-share-dialog', 'width=626,height=436')
      }
    })
  }

  // WhatsApp share button
  const whatsappShareButton = document.querySelector('[data-whatsapp-share]')
  if (whatsappShareButton) {
    whatsappShareButton.addEventListener('click', () => {
      const shareText = whatsappShareButton.getAttribute('data-whatsapp-share')
      if (shareText) {
        // Detect if mobile/tablet to use appropriate WhatsApp URL
        const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
        const whatsappUrl = `https://${isMobileOrTablet ? 'api' : 'web'}.whatsapp.com/send?text=${shareText}`
        window.open(whatsappUrl)
      }
    })
  }

  // LinkedIn share button
  const linkedinShareButton = document.querySelector('[data-linkedin-share]')
  if (linkedinShareButton) {
    linkedinShareButton.addEventListener('click', () => {
      const shareUrl = linkedinShareButton.getAttribute('data-linkedin-share')
      if (shareUrl) {
        window.open(shareUrl, 'linkedin-share-dialog', 'width=626,height=436')
      }
    })
  }

  // Instagram share button (copies link to clipboard)
  const instagramShareButton = document.querySelector('[data-instagram-share]')
  if (instagramShareButton) {
    instagramShareButton.addEventListener('click', () => {
      const shareUrl = instagramShareButton.getAttribute('data-instagram-share')
      if (shareUrl) {
        navigator.clipboard.writeText(shareUrl).then(() => {
          const successMessage = document.getElementById('copy-success-message')
          if (successMessage) {
            successMessage.classList.remove('hidden')
            setTimeout(() => {
              successMessage.classList.add('hidden')
            }, 3000)
          }
        })
      }
    })
  }

  // Native share button (uses Web Share API)
  const nativeShareButton = document.querySelector('[data-native-share]')
  if (nativeShareButton) {
    nativeShareButton.addEventListener('click', () => {
      const shareUrl = nativeShareButton.getAttribute('data-native-share')
      const shareTitle = nativeShareButton.getAttribute('data-native-share-title')

      if (shareUrl && navigator.share) {
        navigator
          .share({
            title: shareTitle || 'GoodCrowd',
            url: shareUrl,
          })
          .catch((error) => {
            // User cancelled the share or share failed
            console.log('Share failed:', error)
          })
      }
    })
  }

  // Initialize Friendly Captcha for inquiry modal
  const captchaContainer = document.querySelector('.frc-captcha:not(.denunciation-captcha)') as HTMLElement
  if (captchaContainer) {
    const sitekey = captchaContainer.getAttribute('data-sitekey')
    if (sitekey) {
      new WidgetInstance(captchaContainer, {
        sitekey: sitekey,
        startMode: 'focus',
        doneCallback: (solution: string) => {
          const hiddenField = document.getElementById('inquiry_captcha_solution') as HTMLInputElement
          if (hiddenField) {
            hiddenField.value = solution
          }
        },
      })
    }
  }

  // Initialize Friendly Captcha for denunciation modal
  const denunciationCaptchaContainer = document.querySelector('.frc-captcha.denunciation-captcha') as HTMLElement
  if (denunciationCaptchaContainer) {
    const sitekey = denunciationCaptchaContainer.getAttribute('data-sitekey')
    if (sitekey) {
      new WidgetInstance(denunciationCaptchaContainer, {
        sitekey: sitekey,
        startMode: 'focus',
        doneCallback: (solution: string) => {
          const hiddenField = document.getElementById('denunciation_captcha_solution') as HTMLInputElement
          if (hiddenField) {
            hiddenField.value = solution
          }
        },
      })
    }
  }
})
